import React, { useState, useMemo, forwardRef } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import useApplePay from "../../hooks/use-apple-pay";

import { PaymentSystem } from "../../constants";
import { getPaymentSystem } from "../../utils/storage";

import classes from "./style.module.css";
import { useRemoteConfig } from "@web-solutions/base-app/hooks/use-remote-config";

export const ApplePayButton = forwardRef(
  (
    {
      product,
      label,
      disabled,
      className,
      onClick,
      onSubmit,
      email,
      onRequireEmail,
    },
    ref
  ) => {
    const paymentSystem = getPaymentSystem();
    const { showApplePayAnimation, collectEmailAfterPayment, requestWalletEmail, requestApplePayEmail, collectApplePayBillingData } = useRemoteConfig();

    // `requestApplePayEmail` is the pre-rename key still configured on most
    // funnels; `requestWalletEmail` is `null` until one is migrated.
    const requestEmail = requestWalletEmail ?? requestApplePayEmail;
    const [address, setAddress] = useState();

    // Stripe-only: when enabled, collect the payer's billing address from the
    // Apple Pay sheet. Stripe returns NOTHING in `billing_details` unless the
    // field is requested, so we request the name — the name request is what makes
    // Apple Pay hand back the billing address — but NOT phone or shipping. Email
    // is requested separately, only when `requestApplePayEmail` is also on.
    const collectBillingData =
      Boolean(collectApplePayBillingData) && paymentSystem === PaymentSystem.STRIPE;

    // Stripe deferred-email flow: when `collectEmailAfterPayment` is enabled an
    // anonymous user (with a billing token) is already created upfront, so the
    // Apple Pay charge needs no email — it is collected afterwards on the
    // email-after-payment page. Don't block the native sheet on a missing email.
    const emailOptional =
      Boolean(collectEmailAfterPayment) && paymentSystem === PaymentSystem.STRIPE;

    const { id, amount, isIntroductory, trial_price_amount, currency } =
      product;

    const options = useMemo(
      () => {
        const totalAmount = !isIntroductory ? amount : trial_price_amount;
        const opts = {
          label,
          amount: totalAmount,
          currency,
          // Email is requested only when `requestApplePayEmail` is on — in the
          // deferred-email flow (to skip the post-payment email page) OR in the
          // billing-data flow. `collectApplePayBillingData` on its own does NOT
          // request the email; it must be paired with `requestApplePayEmail`.
          requestPayerEmail:
            Boolean(requestEmail) && (emailOptional || collectBillingData),
          // Billing-address flow: requesting the name is what makes Apple Pay
          // hand back the billing address in Stripe's `billing_details.address`.
          // We don't request phone or shipping — only the billing address (and
          // the email when `requestApplePayEmail` is also on).
          requestPayerName: collectBillingData,
        };

        // For Stripe + intro/trial flow, surface the price breakdown in the
        // Apple Pay sheet: line 1 = full plan price, line 2 = discount,
        // total = today's intro charge. Skip if math doesn't add up.
        const fullAmount = Number(amount);
        const introAmountNum = Number(trial_price_amount);
        if (
          paymentSystem === PaymentSystem.STRIPE
          && isIntroductory
          && fullAmount > 0
          && introAmountNum >= 0
          && fullAmount > introAmountNum
        ) {
          opts.displayItems = [
            { label: label || 'Plan', amount: fullAmount },
            { label: 'Discount', amount: -(fullAmount - introAmountNum) },
          ];
        }

        return opts;
      },
      [amount, isIntroductory, label, trial_price_amount, currency, paymentSystem, emailOptional, requestEmail, collectBillingData]
    );

    const { paymentRequest } = useApplePay(options, {
      onShippingAddress: (res) => {
        setAddress(res);
      },
      onMethod: (res) => {
        const formData = {
          price_id: id,
          method: "applepay",
          shipping_address: address,
          currency,
          email,
        };

        if (paymentSystem === PaymentSystem.RECURLY) {
          if (res?.id) {
            formData.token_id = res.id;
          }
        } else if (paymentSystem === PaymentSystem.STRIPE) {
          const { trialDays, onetimePriceId } = product;

          const { complete, paymentMethod } = res;
          formData.payment_method_id = paymentMethod?.id;
          formData.trial_period_days = trialDays;
          if (isIntroductory && onetimePriceId) {
            formData.introductory_price_id = onetimePriceId;
          }
          // Deferred-email flow: stash the email Apple Pay returned (if any) so
          // the success handler can attach it and skip the email-after-payment
          // page. Set on every authorization (incl. cleared) to avoid stale data.
          if (emailOptional && typeof window !== "undefined") {
            window.__apple_pay_payer_email__ = res.payerEmail || undefined;
          }
          // Billing-address flow: collect the email and billing address Apple Pay
          // returned on the paymentmethod event so they reach the backend with
          // the payment. (Name comes back too since requesting it is how we get
          // the address, but we don't forward it.)
          if (collectBillingData) {
            const billing = paymentMethod?.billing_details;
            formData.payer_email = res.payerEmail || billing?.email || email || undefined;
            formData.billing_address = billing?.address || undefined;
          }
          complete("success");
        } else if (paymentSystem === PaymentSystem.SOLIDGATE) {
          Object.assign(formData, res);
        }

        onSubmit(formData);
      },
    });

    if (!paymentRequest) {
      return null;
    }

    const handleClickButton = () => {
      if (onClick) {
        onClick();
      }
      if (!email && !emailOptional) {
        onRequireEmail?.();
        return;
      }

      if (paymentSystem === PaymentSystem.RECURLY) {
        paymentRequest.begin();
      } else if (paymentSystem === PaymentSystem.STRIPE) {
        paymentRequest.show();
      } else if (paymentSystem === PaymentSystem.SOLIDGATE) {
        paymentRequest.begin();
      }
    };

    const buttonClassNames = classNames(
      classes["apple-pay-button"],
      {
        [classes["apple-pay-button-animation"]]: showApplePayAnimation,
      },
      className
    );

    return (
      <button
        ref={ref}
        label="apple"
        type="button"
        disabled={disabled}
        className={buttonClassNames}
        onClick={handleClickButton}
      />
    );
  }
);

ApplePayButton.propTypes = {
  product: PropTypes.object.isRequired,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.any,
  onClick: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  email: PropTypes.string,
  onRequireEmail: PropTypes.func,
};

ApplePayButton.defaultProps = {
  disabled: false,
  className: null,
  onClick: null,
  email: null,
  onRequireEmail: null,
};
